import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isDate, parseISO, intlFormat } from 'date-fns'

import {
  ActionFormField,
  Button,
  Loader,
  SegmentedControl,
  Timer,
} from '../../components/transpais'

import { DateType, TripType } from '../../components/transpais/enums'

import global from './BookingGlobal.module.scss'
import styles from './BookingSearch.module.scss'

import CalendarView from './partials/CalendarView'

// Importar hooks y lógica de datos desde archivo separado
import { useBookingSearch } from './hooks/useBookingSearch'
import { useModal } from '../../contexts/modal.context'
import { BASE_URL } from '../../utilities/constants'

// Main component solo UI y eventos
const BookingSearch = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { showModal, hideModal, setupModal } = useModal()

  const {
  // loading (pinpad) removido para no bloquear UI
    localLoading,
    fares,
    count,
    selectedType,
    canContinue,
    bookingOrigin,
    bookingDestination,
    bookingDepartureDate,
    bookingReturningDate,
    handleExit,
    handleChangeTripType,
    handleSelectOrigin,
    handleSelectDestination,
    handleContinueSearch,
    setDates,
    onIncrement,
    onDecrement,
    hasSelectedFares,
    setupModalView,
    showModalView,
    days,
    setSelectedType,
  } = useBookingSearch()

  // Mostrar modal si venimos de cualquier flujo corrupto
  useEffect(() => {
    const problem = location.state?.problem
    if (!problem) return

    let content = null
    switch (problem) {
      case 'cart-corrupt':
        content = <p>Hubo un problema con tu carrito. Inicia de nuevo tu búsqueda.</p>
        break
      case 'trips-corrupt':
        content = <p>Se detectó un problema cargando los viajes. Reinicia tu búsqueda, por favor.</p>
        break
      default:
        content = <p>Ocurrió un problema inesperado. Intenta de nuevo.</p>
    }

    setupModal({
      title: 'Atención',
      content,
      actions: {
        positive: {
          title: 'OK',
          onClick: () => {
            hideModal()
            navigate(`${BASE_URL}/booking/search`, { replace: true, state: {} })
          }
        }
      }
    })
    showModal()
  }, [location.state, setupModal, showModal, hideModal, navigate])

  return (
    <>
  <Loader show={localLoading} />
      <section className={['section-full', 'section-flex'].join(' ')}>
        <div className={[global.top, styles.top].join(' ')}>
          <div className={['logo', styles.logo].join(' ')}>
            <figure className={['logo-image', 'logo-image-transpais-light'].join(' ')} />
            <Timer variant={'clock'} />
          </div>

          <div className={styles.middle}>
            <div className={styles.saleCard}>
              <SegmentedControl
                items={['Sencillo', 'Redondo']}
                selected={selectedType}
                onChange={handleChangeTripType}
              />

              <div className={styles.searchFormFields}>
                <ActionFormField
                  label={'¿De dónde sales?'}
                  value={bookingOrigin?.name ?? 'Selecciona tu origen'}
                  brief={bookingOrigin?.shortcode ?? <FontAwesomeIcon icon={solid('location-dot')} />}
                  onClick={handleSelectOrigin}
                />
                <ActionFormField
                  label={'¿A dónde vas?'}
                  value={bookingDestination?.name ?? 'Selecciona tu destino'}
                  brief={bookingDestination?.shortcode ?? <FontAwesomeIcon icon={solid('location-dot')} />}
                  onClick={handleSelectDestination}
                />
                <ActionFormField
                  label={'Fecha de salida'}
                  value={
                    bookingDepartureDate &&
                    isDate(parseISO(bookingDepartureDate)) &&
                    intlFormat(parseISO(bookingDepartureDate), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  }
                  brief={
                    bookingDepartureDate &&
                    isDate(parseISO(bookingDepartureDate)) &&
                    intlFormat(parseISO(bookingDepartureDate), {
                      month: 'short',
                      day: 'numeric',
                    })
                  }
                  onClick={() => {
                    setupModalView({
                      title: 'Selecciona tus fechas',
                      content: (
                        <CalendarView
                          count={days.current}
                          onChange={setDates}
                          departure={bookingDepartureDate}
                          returning={bookingReturningDate}
                          tripType={selectedType === 0 ? TripType.single : TripType.round}
                          type={DateType.departure}
                          onFinish={() => {
                            if (selectedType !== 1) {
                              handleContinueSearch()
                            }
                          }}
                        />
                      ),
                    })
                    showModalView()
                  }}
                />
                <ActionFormField
                  label={'Fecha de regreso'}
                  value={
                    bookingReturningDate &&
                    isDate(parseISO(bookingReturningDate)) &&
                    intlFormat(parseISO(bookingReturningDate), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  }
                  brief={
                    bookingReturningDate &&
                    isDate(parseISO(bookingReturningDate)) &&
                    intlFormat(parseISO(bookingReturningDate), {
                      month: 'short',
                      day: 'numeric',
                    })
                  }
                  onClick={() => {
                    if (selectedType === 1) {
                      setSelectedType(1)
                    }
                    setupModalView({
                      title: 'Selecciona tus fechas',
                      content: (
                        <CalendarView
                          count={days.current}
                          onChange={setDates}
                          departure={bookingDepartureDate}
                          returning={bookingReturningDate}
                          tripType={selectedType === 0 ? TripType.single : TripType.round}
                          type={DateType.returning}
                          onFinish={handleContinueSearch}
                        />
                      ),
                    })
                    showModalView()
                  }}
                  disabled={selectedType !== 1}
                />
              </div>
            </div>
            <div className={styles.fareCard}>
              <div className={styles.parentContainer}>
                <h2>¿Quiénes viajan?</h2>
                {fares && fares.length > 0 ? (
                  fares.map((fare) => (
                    <div key={fare._id} className={styles.container}>
                      <button className={styles.minusIcon} onClick={() => onDecrement(fare._id)}></button>
                      <span className={styles.label}>{fare.name}</span>
                      <div className={styles.counter}>
                        <span className={styles.count}>{count[fare._id] || 0}</span>
                        <button className={styles.plusIcon} onClick={() => onIncrement(fare._id)}></button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No hay fares disponibles favor de contactar con el administrador</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <footer className={global.bottom}>
          <div className={styles.left}>
            <Button to={'/'} type="neutral" left-icon="chevron-left">
              Volver
            </Button>
          </div>
          <div className={global.middle}>
            <Button onClick={handleExit} type="positive" right-icon="house">
              Ir a inicio
            </Button>
          </div>
          <div className={styles.right}>
            <Button onClick={handleContinueSearch} type="positive" right-icon="chevron-right" disabled={!canContinue || !hasSelectedFares}>
              Buscar
            </Button>
          </div>
        </footer>
      </section>
    </>
  )
}

export default BookingSearch